import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Edit3, FileText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";

import Meta from "../../components/Meta";
import api from "../../utils/axiosInstance";
import { getApiErrorMessage } from "../../utils/errorMessages";

import "./AdminLegalScreen.css";

function formatDate(value = "") {
  if (!value) return "Not set";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Not set";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export default function AdminLegalListScreen() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadPages = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await api.get("/admin/legal");
      setPages(Array.isArray(data?.pages) ? data.pages : []);
    } catch (err) {
      const message = getApiErrorMessage(err, {
        fallback:
          "We could not load legal pages right now. Please try again later.",
      });

      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  return (
    <main className="admin-legal">
      <Meta title="Legal pages | Admin | Academy Costumes London" robots="noindex" />

      <header className="admin-legal__hero">
        <div>
          <p>Content</p>
          <h1>Legal Pages</h1>
          <span>Manage temporary placeholder legal content.</span>
        </div>

        <button
          type="button"
          className="admin-legal__button admin-legal__button--ghost"
          onClick={loadPages}
          disabled={loading}
        >
          {loading ? (
            <Loader2 size={16} className="admin-legal__spin" />
          ) : (
            <RefreshCw size={16} />
          )}
          Refresh
        </button>
      </header>

      {error ? <p className="admin-legal__notice">{error}</p> : null}

      <section className="admin-legal__grid" aria-label="Legal pages">
        {loading && !pages.length ? (
          <div className="admin-legal__state">
            <Loader2 className="admin-legal__spin" />
            <p>Loading legal pages...</p>
          </div>
        ) : (
          pages.map((page) => (
            <article className="admin-legal__card" key={page.slug}>
              <FileText size={22} />
              <div>
                <p>{page.status || "published"}</p>
                <h2>{page.title}</h2>
                <span>Last updated {formatDate(page.lastUpdated)}</span>
              </div>

              <Link
                className="admin-legal__button"
                to={`/admin/legal/${encodeURIComponent(page.slug)}/edit`}
              >
                <Edit3 size={16} />
                Edit
              </Link>
            </article>
          ))
        )}
      </section>
    </main>
  );
}
